import { ChangeDetectorRef } from '@angular/core';
import { finalize, takeUntil } from 'rxjs/operators';
import { ChatSource } from '../../models/chat.model';
import { ChatQueryUiResult } from '../../models/chat-ui.model';
import { ChatService } from '../../services/chat/api/chat.service';
import {
  ChatMessageFlowEvent,
  ChatMessageFlowService,
} from '../../services/chat/flow/chat-message-flow.service';
import { ChatSourceMapperService } from '../../services/chat/mapping/chat-source-mapper.service';
import { ChatDomEffectsService } from '../../services/chat/presentation/chat-dom-effects.service';
import { ChatStorageService } from '../../services/chat/session/chat-storage.service';
import { FrontendLoggerService } from '../../services/frontend-logger.service';
import { ChatConversationMetadata, formatChatError } from './chat-runtime.models';
import { ChatQueryStatePort } from './chat-runtime.ports';
import { ChatRequestTracker } from './chat-request.tracker';
import { ChatRoleComposerController } from './chat-role-composer.controller';
import { ChatSessionController } from './chat-session.controller';

export class ChatQueryController {
  constructor(
    private readonly state: ChatQueryStatePort,
    private readonly messageFlow: ChatMessageFlowService,
    private readonly chatService: ChatService,
    private readonly storage: ChatStorageService,
    private readonly sourceMapper: ChatSourceMapperService,
    private readonly dom: ChatDomEffectsService,
    private readonly logger: FrontendLoggerService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly roles: ChatRoleComposerController,
    private readonly requests: ChatRequestTracker,
    private readonly sessions: ChatSessionController
  ) {}

  send(overrideSelectMode?: string): void {
    if (this.state.chatForm.invalid || this.state.isLoading || this.roles.isCurrentModeBlocked) {
      return;
    }
    const raw = this.state.chatForm.get('messageInput')?.value;
    const content = typeof raw === 'string' ? raw.trim() : '';
    if (!content) return;

    this.state.pendingAutoTitleText = this.state.messages.length === 0 ? content : null;
    this.logger.info(`Usuario envia mensaje: ${content}`, 'ChatComponent.sendMessage');
    this.state.chatForm.reset();
    this.prepareRequest();
    const override: 'generar_resumen' | undefined =
      overrideSelectMode === 'generar_resumen' || this.state.armedGenerateSummary
        ? 'generar_resumen'
        : undefined;
    this.state.armedGenerateSummary = false;

    this.messageFlow.submitMessage({
      messageContent: content,
      selectedInputMode: this.state.selectedInputMode,
      topK: this.state.topK,
      limitedDocId: this.state.limitedDocId,
      selectedChunkId: this.state.selectedChunkId,
      expertRole: this.state.selectedExpertRole,
      overrideSelectMode: override,
    }).pipe(takeUntil(this.state.destroy$))
      .subscribe((event) => this.applyFlowEvent(event));
  }

  applyFlowEvent(event: ChatMessageFlowEvent): void {
    if (event.kind === 'user-message') {
      this.state.messages.push(event.message);
      this.sessions.scrollToBottom();
      return;
    }
    if (event.kind === 'csv-modal') {
      this.state.isLoading = false;
      this.state.pendingQuery = event.result.pendingQuery;
      this.state.csvDocuments = event.result.csvDocuments;
      this.state.showCsvModal = event.result.showCsvModal;
      return;
    }
    if (event.kind === 'query-result') {
      this.state.isLoading = false;
      this.applyQueryResult(event.uiResult);
      this.sessions.scrollToBottom();
      return;
    }
    if (event.kind === 'plan-limit') {
      this.state.isLoading = false;
      this.sessions.showPlanLimit(event.error);
      return;
    }
    this.state.isLoading = false;
    this.state.messages.push(event.message);
    this.sessions.scrollToBottom();
  }

  applyQueryResult(result: ChatQueryUiResult): void {
    if (result.kind === 'rag') {
      this.logger.info('Respuesta tipo RAG recibida', 'ChatComponent.sendMessage');
      this.storage.setSessionId(result.sessionId || '0');
      this.sessions.maybeAutoTitle(result.sessionId);
      this.state.messages.push(result.message);
      this.state.currentSources = result.sources;
      this.state.selectedSource = result.selectedSource;
      this.state.selectedChunkId = result.selectedChunkId;
      this.state.showDetails = result.showDetails || this.state.showDetails;
      this.state.lastAppliedExpertRole = result.appliedExpertRole;
      this.changeDetector.markForCheck();
      this.state.showFeedback = false;
      return;
    }
    if (result.kind === 'csv') {
      this.logger.info('Respuesta tipo CSV recibida', 'ChatComponent.sendMessage');
      this.state.pendingQuery = result.pendingQuery;
      this.state.csvDocuments = result.csvDocuments;
      this.logger.info(
        `[ChatComponent] csvDocuments recibidos: ${JSON.stringify(this.state.csvDocuments)}`
      );
      this.state.showCsvModal = result.showCsvModal;
      return;
    }
    if (result.kind === 'error') {
      this.logger.error(
        `Respuesta tipo error recibida: ${result.error}`,
        'ChatComponent.sendMessage'
      );
      this.state.messages.push(result.message);
      return;
    }
    this.logger.warn('Respuesta no RAG recibida', 'ChatComponent.sendMessage');
    this.state.messages.push(result.message);
  }

  parseMetadata(metadata: unknown): ChatConversationMetadata {
    if (!metadata) return {};
    if (typeof metadata === 'object') return metadata as ChatConversationMetadata;
    if (typeof metadata !== 'string') return {};
    try {
      const parsed = JSON.parse(metadata) as unknown;
      return parsed && typeof parsed === 'object'
        ? parsed as ChatConversationMetadata
        : {};
    } catch (error) {
      this.logger.warn(
        'No se pudo parsear metadata de conversación: ' + formatChatError(error),
        'ChatComponent.parseConversationMetadata'
      );
      return {};
    }
  }

  loadSelectedChunks(): void {
    const id = this.storage.getSelectedMessageId() || '0';
    this.logger.info(
      `Detalles desplegados para el mensaje seleccionado: ${id}`,
      'ChatComponent.toggleDetails'
    );
    this.requests.start();
    this.chatService.getConversationSummary(id).pipe(
      finalize(() => this.requests.end()),
      takeUntil(this.state.destroy$)
    ).subscribe({
      next: (response: any) => this.applyConversationSummary(response, id),
      error: (error) => this.logger.error(
        'Error al obtener resumen de conversación: ' + formatChatError(error),
        'ChatComponent.toggleDetails'
      ),
    });
  }

  toggleDetails(): void {
    this.state.showDetails = !this.state.showDetails;
    if (this.state.showDetails) {
      this.openDetailsPanel();
    } else {
      try { this.dom.removeBodyClass('no-scroll'); } catch (error) {
        this.logger.error(
          'Error al quitar clase no-scroll del body: ' + formatChatError(error),
          'ChatComponent.toggleDetails'
        );
      }
    }
  }

  private prepareRequest(): void {
    this.state.isLoading = true;
    this.state.error = null;
    this.state.planLimitError = null;
    this.state.feedbackSent = false;
    this.state.showFeedback = false;
    this.state.currentSources = [];
    this.state.selectedSource = null;
    this.state.lastRequestedExpertRole = this.state.selectedExpertRole;
  }

  private applyConversationSummary(response: any, id: string): void {
    this.logger.info(
      `[ChatComponent] Resumen de conversación recibido: ${JSON.stringify(response)}`
    );
    const metadata = this.parseMetadata(response.metadata);
    this.state.currentSources = this.sourceMapper.mapRagChunks(metadata.relevant_chunks || []);
    const candidateId = metadata.selected_chunk?.metadata?.id
      || metadata.selected_chunk_id
      || this.state.selectedChunkId;
    if (candidateId) this.selectChunk(candidateId);
    this.logger.info(
      `Resumen de conversación recibido para id ${id}`,
      'ChatComponent.toggleDetails'
    );
  }

  private selectChunk(chunkId: string): void {
    const index = this.state.currentSources.findIndex(
      (source: ChatSource) => source.metadata?.id === chunkId
    );
    if (index < 0) return;
    this.state.currentSources[index].selected = true;
    this.state.selectedSource = this.state.currentSources[index];
    this.state.showDetails = true;
  }

  private openDetailsPanel(): void {
    try { this.dom.addBodyClass('no-scroll'); } catch (error) {
      this.logger.error(
        'Error al añadir clase no-scroll al body: ' + formatChatError(error),
        'ChatComponent.toggleDetails'
      );
    }
    setTimeout(() => {
      const element = this.state.detailsPanel?.nativeElement;
      if (element) {
        try {
          this.dom.scrollElement(element, { behavior: 'smooth', block: 'nearest' });
        } catch (error) {
          this.logger.error(
            'Error al hacer scroll al panel de detalles: ' + formatChatError(error),
            'ChatComponent.toggleDetails'
          );
        }
      } else {
        this.dom.scrollElementById('chat-details');
      }
    }, 0);
  }
}
